/**
 * Defines shared type definitions used across the application
 */

// User Role Hierarchy
export type UserRole = 'guest' | 'agent' | 'manager' | 'imo' | 'agency-owner' | 'upline' | 'admin' | 'super-admin';

// Role Hierarchy Mapping
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  'guest': 0,
  'agent': 1,
  'manager': 2,
  'imo': 3,
  'agency-owner': 4,
  'upline': 5,
  'admin': 6,
  'super-admin': 7
};

// User Status
export type UserStatus = 'active' | 'inactive' | 'suspended';

// Valid Carrier Types
export type ValidCarrier = 'Humana' | 'Aetna' | 'UnitedHealthcare' | 'Cigna' | 'WellCare' | 'Anthem';
export type HospitalCarrier = 'Manhattan Life' | 'Medico' | 'Liberty Bankers' | 'Other';

// Permission Types
export type Permission = 'read' | 'write' | 'delete' | 'manage';
export type ResourceType = 'users' | 'sales' | 'reports' | 'settings';
export type PermissionScope = 'own' | 'team' | 'agency' | 'all';

// Role Settings Interface
export interface RoleSettings {
  role: UserRole;
  permissions: Permission[];
  scope: PermissionScope;
  canManageRoles: UserRole[];
}

// Default Role Settings
export const DEFAULT_ROLE_SETTINGS: Record<UserRole, RoleSettings> = {
  'guest': {
    role: 'guest',
    permissions: ['read'],
    scope: 'own',
    canManageRoles: []
  },
  'agent': {
    role: 'agent',
    permissions: ['read', 'write'],
    scope: 'own',
    canManageRoles: []
  },
  'manager': {
    role: 'manager',
    permissions: ['read', 'write', 'manage'],
    scope: 'team',
    canManageRoles: ['agent']
  },
  'imo': {
    role: 'imo',
    permissions: ['read', 'write', 'manage'],
    scope: 'agency',
    canManageRoles: ['agent', 'manager']
  },
  'agency-owner': {
    role: 'agency-owner',
    permissions: ['read', 'write', 'delete', 'manage'],
    scope: 'agency',
    canManageRoles: ['agent', 'manager', 'imo']
  },
  'upline': {
    role: 'upline',
    permissions: ['read', 'write', 'manage'],
    scope: 'team',
    canManageRoles: ['agent']
  },
  'admin': {
    role: 'admin',
    permissions: ['read', 'write', 'delete', 'manage'],
    scope: 'all',
    canManageRoles: ['guest', 'agent', 'manager', 'imo', 'agency-owner', 'upline']
  },
  'super-admin': {
    role: 'super-admin',
    permissions: ['read', 'write', 'delete', 'manage'],
    scope: 'all',
    canManageRoles: ['guest', 'agent', 'manager', 'imo', 'agency-owner', 'upline', 'admin']
  }
};

/**
 * Check if a user has sufficient role level
 * @param userRole Current user's role
 * @param requiredRole Required role for the operation
 * @returns Boolean indicating role sufficiency
 */
export function hasSufficientRole(
  userRole: UserRole, 
  requiredRole: UserRole
): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Get the numeric level of a role
 * @param role User role
 * @returns Numeric level in hierarchy
 */
export function getRoleLevel(role: UserRole): number {
  return ROLE_HIERARCHY[role];
}

/**
 * Type guard to check if a string is a valid UserRole
 * @param role Role string to check
 * @returns Type predicate for UserRole
 */
export function isUserRole(role: string): role is UserRole {
  return Object.keys(ROLE_HIERARCHY).includes(role as UserRole);
}

/**
 * Check if a user has required access level
 * @param userRole Current user's role
 * @param requiredRole Required role for access
 */
export const hasRequiredAccess = hasSufficientRole;

/**
 * Check if a user has a specific permission
 * @param userRole User's role
 * @param permission Permission to check
 * @returns Boolean indicating if user has permission
 */
export function hasPermission(userRole: UserRole, permission: Permission): boolean {
  return DEFAULT_ROLE_SETTINGS[userRole].permissions.includes(permission);
}

/**
 * Check if one role can manage another
 * @param managerRole Role attempting to manage
 * @param targetRole Role being managed
 * @returns Boolean indicating if management is allowed
 */
export function canManageRole(managerRole: UserRole, targetRole: UserRole): boolean {
  return DEFAULT_ROLE_SETTINGS[managerRole].canManageRoles.includes(targetRole);
}

/**
 * Check if an action requires approval based on role
 * @param userRole User's role
 * @returns Boolean indicating if approval is required
 */
export function requiresApproval(userRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] < ROLE_HIERARCHY['admin'];
}

// Generic interfaces
export interface Identifiable {
  id: string;
}

export interface Timestamped {
  createdAt: Date;
  updatedAt: Date;
}

export interface SoftDeletable {
  isDeleted?: boolean;
  deletedAt?: Date;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ApiResult<T> {
  data?: T;
  error?: string;
  success: boolean;
}

// Database Types
export interface FirestoreUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  hierarchyPath: string;
  emailVerified: boolean;
  metadata?: {
    phoneNumber?: string;
    timezone?: string;
    lastLogin?: Date;
    registrationSource?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
}

export interface CarrierSale {
  carrier: ValidCarrier | HospitalCarrier;
  count: number;
  revenue: number;
}

export type DatabaseCarrierSale = CarrierSale;
